import { MouseEvent, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { InlineEditableInput } from "@/components/ui/InlineEditableInput";

import { Asset } from "../types";
import { formatBytes } from "../utils";
import { groupAssetsByVariant } from "../assetVariants";
import { AssetPreview } from "./AssetPreview";

type AssetGridProps = {
  assets: Asset[];
  selectedAssetIds: string[];
  onAssetClick: (asset: Asset) => void;
  onAssetContextMenu: (id: string, event: MouseEvent) => void;
  onToggleAssetSelection: (id: string, checked: boolean) => void;
  onRenameAsset: (id: string, name: string) => void;
  renameAssetId: string | null;
  showCheckbox?: boolean;
  enableContextMenu?: boolean;
};

export function AssetGrid({
  assets,
  selectedAssetIds,
  onAssetClick,
  onAssetContextMenu,
  onToggleAssetSelection,
  onRenameAsset,
  renameAssetId,
  showCheckbox = true,
  enableContextMenu = true,
}: AssetGridProps) {
  const groups = useMemo(() => groupAssetsByVariant(assets), [assets]);
  const [expandedAssetIds, setExpandedAssetIds] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleExpanded = (assetId: string) => {
    setExpandedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const renderAssetCard = (
    asset: Asset,
    options?: { variants?: Asset[]; isVariant?: boolean },
  ) => {
    const variants = options?.variants ?? [];
    const isVariant = options?.isVariant ?? false;
    const isSelected = selectedAssetIds.includes(asset.id);
    const isRenaming = renameAssetId === asset.id;
    const hasVariants = variants.length > 0;
    const expanded = expandedAssetIds.has(asset.id);

    return (
      <div
        key={asset.id}
        className={`flex cursor-pointer flex-col gap-2 rounded border border-gray-300 p-3 ${
          isSelected ? "bg-pinkCustom/15" : "hover:bg-pinkCustom/5"
        } ${isVariant ? "ml-4 border-gray-200 bg-gray-50 p-2" : ""}`}
        onClick={() => onAssetClick(asset)}
        onContextMenu={(e) => {
          if (!enableContextMenu) return;
          e.preventDefault();
          onAssetContextMenu(asset.id, e);
        }}
      >
        <div className="flex items-center gap-2">
          {showCheckbox ? (
            <input
              type="checkbox"
              className="accent-pinkCustom h-4 w-4"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleAssetSelection(asset.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
          {hasVariants ? (
            <button
              type="button"
              className="rounded p-0.5 text-gray-500 hover:bg-gray-100"
              aria-label={expanded ? "Hide variants" : "Show variants"}
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(asset.id);
              }}
            >
              <ChevronRight
                size={15}
                className={`transition-transform ${expanded ? "rotate-90" : ""}`}
              />
            </button>
          ) : null}
          <AssetPreview asset={asset} size={isVariant ? 32 : 40} />
          {isVariant ? (
            <span className="text-[10px] tracking-wide text-gray-400 uppercase">
              Variant
            </span>
          ) : null}
        </div>
        <div
          className="truncate text-sm font-semibold"
          onClick={(e) => e.stopPropagation()}
        >
          <InlineEditableInput
            value={asset.originalFilename}
            onSave={(next) => onRenameAsset(asset.id, next)}
            className="truncate"
            startEditing={isRenaming}
            enableClickToEdit={false}
          />
        </div>
        <div className="text-[11px] text-gray-500">
          {formatBytes(asset.size)}
        </div>
      </div>
    );
  };

  return (
    <>
      {groups.map(({ asset, variants }) => {
        const expanded = expandedAssetIds.has(asset.id);
        return (
          <div key={asset.id} className="flex min-w-0 flex-col gap-2">
            {renderAssetCard(asset, { variants })}
            <AnimatePresence initial={false}>
              {expanded && variants.length ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="border-pinkCustom/20 flex flex-col gap-2 overflow-hidden border-l-2"
                >
                  {variants.map((variant) =>
                    renderAssetCard(variant, { isVariant: true }),
                  )}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </>
  );
}
